import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Megaphone, Trash2, Eye, Calendar, Plus } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { expiraEmFormat, timeAgo } from '../utils/feedHelpers';

const STATUS_BADGE = {
  ativo: { label: 'ATIVO', cor: 'bg-green-100 text-green-800' },
  expirado: { label: 'EXPIRADO', cor: 'bg-gray-200 text-gray-600' },
  removido: { label: 'REMOVIDO', cor: 'bg-red-100 text-red-700' },
};

export default function MeusPostsFeed() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const negocioId = search.get('negocio_id') || '';

  const [posts, setPosts] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});

  async function reload() {
    try {
      const r = await api.get('/v2/empreendedor/feed/posts', { params: negocioId ? { negocio_id: negocioId } : {} });
      setPosts(r.data || []);
    } catch {
      setPosts([]);
    }
  }

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    Promise.all([
      api.get('/v2/feed/categorias').then((r) => {
        const m = {}; (r.data?.categorias || []).forEach((c) => { m[c.key] = c.label; });
        setLabels(m);
      }).catch(() => {}),
      reload(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, negocioId, navigate]);

  const stats = useMemo(() => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const inicioMes = new Date(); inicioMes.setDate(1); inicioMes.setHours(0, 0, 0, 0);
    const ativos_hoje = posts.filter((p) => p.status === 'ativo' && new Date(p.criado_em).getTime() >= hoje.getTime()).length;
    const este_mes = posts.filter((p) => new Date(p.criado_em).getTime() >= inicioMes.getTime()).length;
    return { ativos_hoje, este_mes };
  }, [posts]);

  async function removerPost(id) {
    if (!window.confirm('Remover este post?')) return;
    // optimistic update
    setRemoving((r) => ({ ...r, [id]: true }));
    const snapshot = posts;
    setPosts((p) => p.map((x) => (x.id === id ? { ...x, status: 'removido' } : x)));
    try {
      await api.delete(`/v2/feed/posts/${id}`);
      toast.success('Post removido.');
    } catch (e) {
      setPosts(snapshot);
      toast.error(e?.response?.data?.detail || 'Erro ao remover.');
    } finally {
      setRemoving((r) => { const c = { ...r }; delete c[id]; return c; });
    }
  }

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando…</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="feed-meus-posts">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <header className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-purple-600" /> Meus posts no Feed
            </h1>
            <p className="text-sm text-gray-500 mt-1">Histórico dos últimos 30 dias</p>
          </div>
          <Link
            to={negocioId ? `/empreendedor/feed/novo?negocio_id=${encodeURIComponent(negocioId)}` : '/empreendedor/feed/novo'}
            data-testid="feed-meus-novo-btn"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition"
          >
            <Plus className="w-4 h-4" /> Novo post
          </Link>
        </header>

        {/* stats */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-white rounded-xl border border-gray-100 p-3" data-testid="feed-stats-ativos-hoje">
            <div className="text-xs text-gray-500">Posts ativos hoje</div>
            <div className="text-2xl font-bold text-purple-700">{stats.ativos_hoje}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3" data-testid="feed-stats-este-mes">
            <div className="text-xs text-gray-500">Posts este mês</div>
            <div className="text-2xl font-bold text-purple-700">{stats.este_mes}</div>
          </div>
        </div>

        {posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <p className="text-gray-700 font-medium">Nenhum post nos últimos 30 dias.</p>
            <p className="text-sm text-gray-500 mt-1">Comece a publicar para aparecer no feed da cidade.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {posts.map((p) => {
              const statusInfo = STATUS_BADGE[p.status] || STATUS_BADGE.ativo;
              const timer = expiraEmFormat(p.expira_em);
              const textoTrunc = (p.texto || '').length > 100 ? `${p.texto.slice(0, 100).trim()}…` : (p.texto || '');
              return (
                <div
                  key={p.id}
                  data-testid={`feed-meu-post-${p.id}`}
                  className="bg-white rounded-xl border border-gray-100 p-3 flex items-start gap-3"
                >
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusInfo.cor} shrink-0`}>
                    {statusInfo.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-purple-700 font-medium">{labels[p.categoria_post] || p.categoria_post}</div>
                    <div className="text-sm mt-0.5 text-gray-800">{textoTrunc}</div>
                    <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span><Calendar className="inline w-3 h-3 mr-0.5" />{timeAgo(p.criado_em)}</span>
                      {p.status === 'ativo' && p.expira_em && (
                        <span className={timer.cor}>· {timer.label}</span>
                      )}
                      {p.status === 'expirado' && p.expira_em && (
                        <span>· Expirou {new Date(p.expira_em).toLocaleDateString('pt-BR')}</span>
                      )}
                      {typeof p.views === 'number' && (
                        <span><Eye className="inline w-3 h-3 mr-0.5" />{p.views} {p.views === 1 ? 'visualização' : 'visualizações'}</span>
                      )}
                    </div>
                  </div>
                  {p.status === 'ativo' && (
                    <button
                      onClick={() => removerPost(p.id)}
                      disabled={removing[p.id]}
                      data-testid={`feed-remover-${p.id}`}
                      className="shrink-0 p-2 rounded-lg text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                      title="Remover post"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
